/* eslint-disable @typescript-eslint/no-explicit-any -- aligné U3106 */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import U2000DefaultForm from "../../../u2000Core/components/formControls/U2000DefaultForm";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../../u2000Core/reducer/formReducer/U2000LUState";
import { NS, U3134ROLES } from "../../U3134";
import U3134WorkInProgressBody from "./u3134WorkInProgress/U3134WorkInProgressBody";
import { U3134WorkInProgressFormToolBar } from "./u3134WorkInProgress/U3134WorkInProgressFormToolBar";
import { useU3134Context } from "../../U3134Context";
import U2000AuditPanelComponent from "../../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  detailToU3134FormRow,
  doGetAuditsData,
  isValidUuid,
  isU3134HonorariumFixedPriceLine,
  isU3134InvoicedLine,
  u3134FormRowToSearchGridRow,
  u3134MergeInvoiceFromSearchRow,
  luHandleAppiError,
  U3134_TRANSACTION_KIND,
  type U3134WorkInProgressFormRow,
  type U3134WorkInProgressLineDetail,
  type U3134WorkInProgressSearchRow,
} from "../../U3134Service";
import {
  useU3134GetHandlers,
  useU3134SaveHandlers,
  useU3134SearchHandlers,
} from "../../U3134UseApiHandlers";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../../u2000Core/components/tab/U2000Tab";

export interface U3134WorkInProgressFormExtendedHandlers extends U2000FormStateHandlersReturn<U3134WorkInProgressFormRow> {
  modifyWorkInProgressLine: () => void;
}

const U3134WorkInProgressForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3134Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();

  const validateFormRow = (row: U3134WorkInProgressFormRow, editMode: U2000FormStateEnumEditMode) => {
    if (editMode === U2000FormStateEnumEditMode.EDIT) {
      if (isU3134InvoicedLine(row)) {
        return {};
      }
      return {};
    }

    const result: Record<string, string> = {};
    if (!row.project?.id?.trim()) {
      result["project"] = t("errProjectRequired", { ns: NS, defaultValue: isFr ? "Le projet est obligatoire." : "Project is required." });
    }
    if (!row.transactionDate?.trim()) {
      result["transactionDate"] = t("errTransactionDateRequired", { ns: NS });
    }
    if (!Number.isFinite(row.quantity)) {
      result["quantity"] = t("errNumberRequired", { ns: NS });
    }
    if (row.transactionKind === U3134_TRANSACTION_KIND.Honorarium) {
      if (row.hourly === true && !Number.isFinite(row.sellingRate ?? NaN)) {
        result["sellingRate"] = t("errNumberRequired", { ns: NS });
      }
      if (isU3134HonorariumFixedPriceLine(row) && !Number.isFinite(row.costRate ?? NaN)) {
        result["costRate"] = t("errNumberRequired", { ns: NS });
      }
      if (!Number.isFinite(row.costMarkupFactor)) {
        result["costMarkupFactor"] = t("errNumberRequired", { ns: NS });
      }
    } else if (row.transactionKind === U3134_TRANSACTION_KIND.Expense) {
      if (!Number.isFinite(row.sellingMarkupFactor)) {
        result["sellingMarkupFactor"] = t("errNumberRequired", { ns: NS });
      }
    } else if (row.transactionKind === U3134_TRANSACTION_KIND.Equipment) {
      if (!Number.isFinite(row.sellingMarkupFactor)) {
        result["sellingMarkupFactor"] = t("errNumberRequired", { ns: NS });
      }
    }
    if (!Number.isFinite(row.amount)) {
      result["amount"] = t("errNumberRequired", { ns: NS });
    }
    if (!Number.isFinite(row.costAmount)) {
      result["costAmount"] = t("errNumberRequired", { ns: NS });
    }
    if ((row.remark ?? "").length > 500) {
      result["remark"] = t("errRemarkMax", { ns: NS });
    }
    if (row.taskId.trim() !== "" && !isValidUuid(row.taskId)) {
      result["taskId"] = t("errUuidInvalid", { ns: NS });
    }
    if (row.transactionKind === U3134_TRANSACTION_KIND.Honorarium) {
      if (row.honorariumEntryKind == null) {
        result["honorariumEntryKind"] = t("errHonorariumEntryKindRequired", { ns: NS });
      }
    }
    return result;
  };

  const u3134BL: U2000FormSBusinessHandlers<U3134WorkInProgressFormRow> = {
    newRecord: () => {
      throw new Error("Manual U3134 line creation is not supported");
    },
    validateForm: (row) => validateFormRow(row, U2000FormStateEnumEditMode.EDIT),
    saveForme: async (row: U3134WorkInProgressFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.EDIT && isU3134InvoicedLine(row)) {
        toast.info(t("msgInvoicedLineNotEditable", { ns: NS }));
        return;
      }
      const errors = validateFormRow(row, editMode);
      if (Object.keys(errors).length > 0) {
        u3134FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = u3134FormExtended.getOldData() as U3134WorkInProgressFormRow | null;
      if (old) {
        u3134FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3134") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate("/U3134");
    },
  };

  const u3134FormHandlers = useU2000FormStateHandlers<U3134WorkInProgressFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3134BL,
  });

  const u3134FormExtended: U3134WorkInProgressFormExtendedHandlers = {
    ...u3134FormHandlers,
    modifyWorkInProgressLine: () => {
      const row = u3134FormExtended.getData();
      if (isU3134InvoicedLine(row)) {
        toast.info(t("msgInvoicedLineNotEditable", { ns: NS }));
        return;
      }
      u3134FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
  };

  const handleGet = useU3134GetHandlers<U3134WorkInProgressFormRow>({
    onSuccess: (row: U3134WorkInProgressFormRow) => {
      let normalized = detailToU3134FormRow(row as unknown as U3134WorkInProgressLineDetail);
      const fromGrid = gridStateHandlers.getData().find((r) => r.id === normalized.id);
      normalized = u3134MergeInvoiceFromSearchRow(normalized, fromGrid);
      gridStateHandlers.setDataById(normalized.id, u3134FormRowToSearchGridRow(normalized, fromGrid));
      u3134FormExtended.setData(normalized);
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(normalized.id));
      navigate(newPath, { replace: true });
      const p = normalized.project;
      const crumbLabel = p?.projetNumber ? `${p.projetNumber} — ${p.projetName ?? ""}` : normalized.id;
      setLogicalUnitBreadcrumb([
        { href: "/U3134", label: listTitle },
        { label: crumbLabel },
      ]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (isU3134InvoicedLine(normalized)) {
          toast.info(t("msgInvoicedLineNotEditable", { ns: NS }));
          luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        } else {
          u3134FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
          luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        }
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSearch = useU3134SearchHandlers<U3134WorkInProgressSearchRow>({
    onSuccess: (searchResult: U2000SearchResult<U3134WorkInProgressSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      if (searchResult.data[idx]?.id) {
        handleGet.getData(searchResult.data[idx].id);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU3134SaveHandlers({
    onSuccess: (row: U3134WorkInProgressFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = detailToU3134FormRow(row as unknown as U3134WorkInProgressLineDetail);
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        const fromGrid = gridStateHandlers.getData().find((r) => r.id === row.id);
        gridStateHandlers.setDataById(row.id, u3134FormRowToSearchGridRow(normalized, fromGrid));
      }
      u3134FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3134") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3134FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3134FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      toast.info(t("msgManualCreateNotAllowed", { ns: NS }));
      navigate("/U3134", { replace: true });
      return;
    }
    handleGet.getData(id);
  }, [id]);

  if (u3134FormExtended.getData() === null) {
    return null;
  }

  const formDataId = u3134FormExtended.getData()?.id?.trim() ?? "";
  const formTabs = [
    hasRole(U3134ROLES.AUDITS) &&
      formDataId && {
        id: "tabAudits",
        label: t("tabAudits", { ns: NS }),
        content: <U2000AuditPanelComponent id={formDataId} doGetAuditsData={doGetAuditsData} toastName="U3134WorkInProgressAudit" />,
      },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U2000DefaultForm
        actionsBtns={<U3134WorkInProgressFormToolBar formStateHandlers={u3134FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3134FormExtended as any}
      >
        <U3134WorkInProgressBody formStateHandlers={u3134FormExtended as any} t={t} />
        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3134WorkInProgressForm;
